import Path from "./Path";
import StatusValidation from "./StatusValidation";
import Cookies from "js-cookie";
import { track } from "./ActivityTracker";

function setBearerIfPresent(header, tokenEnabled) {
	if (!tokenEnabled) return;
	const raw = Cookies.get("_cb_search_token");
	if (raw && raw !== "undefined" && raw !== "null") {
		header["Authorization"] = "Bearer " + raw;
	}
}

function trackApi(method, endpoint, httpStatus, failed, errorMessage) {
	try {
		if (!endpoint || String(endpoint).includes("api/v1/activity/batch")) return;
		track({
			eventType: failed ? "api.request.failed" : "api.request.completed",
			eventCategory: "api",
			action: failed ? "API failed" : "API completed",
			httpMethod: method,
			apiEndpoint: endpoint,
			httpStatusCode: httpStatus,
			status: failed ? "failure" : "success",
			errorMessage: errorMessage ? String(errorMessage).slice(0, 200) : null,
		});
	} catch (_) {
		/* never break API */
	}
}

export function PostData(pathValue, type, userData, formData = false, token = true) {
	return new Promise((resolve, reject) => {
		let header = {};
		setBearerIfPresent(header, token);
		if (!formData) {
			header["Content-Type"] = "application/json";
		}
		fetch(Path(pathValue) + type, {
			method: "POST",
			body: formData ? userData : JSON.stringify(userData),
			headers: header,
		})
			.then((response) => {
				const failed = response.status >= 400;
				trackApi("POST", type, response.status, failed);
				return StatusValidation(response);
			})
			.then((responseJson) => {
				resolve(responseJson);
			})
			.catch((error) => {
				trackApi("POST", type, 0, true, error?.message);
				console.log(error);
				reject(error);
			});
	});
}

export function DownloadData(pathValue, type, userData, formData = false, token = true) {
	return new Promise(async (resolve, reject) => {
	  try {
		 let header = {};
		 setBearerIfPresent(header, token);
		 if (!formData) {
			header["Content-Type"] = "application/json";
		 }
 
		 const response = await fetch(Path(pathValue) + type, {
			method: "POST",
			body: formData ? userData : JSON.stringify(userData),
			headers: header,
		 });
 
		 if (!response.ok) {
			trackApi("POST", type, response.status, true);
			const errorText = await response.text();
			const trimmed = (errorText || "").trim();
			if (/^error code:\s*52[0-9]/i.test(trimmed)) {
				throw new Error(
					"Export timed out or the server was unreachable (Cloudflare 52x). Try again or export a single conversation."
				);
			}
			throw new Error(trimmed || "Request failed");
		 }
 
		 const contentType = response.headers.get("content-type") || "";
		 const contentDisposition = response.headers.get("content-disposition") || "";
		 const ctLower = contentType.toLowerCase();
		 const cdLower = contentDisposition.toLowerCase();
		 const isBinaryExport =
			ctLower.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") ||
			ctLower.includes("application/octet-stream") ||
			cdLower.includes(".xlsx") ||
			cdLower.includes("spreadsheetml") ||
			cdLower.includes("attachment");
		 let data;

		 if (ctLower.includes("application/json")) {
			data = await response.json();
		 } else if (isBinaryExport) {
			data = await response.arrayBuffer();
		 } else {
			data = await response.text();
		 }

		 trackApi("POST", type, response.status, false);
		 resolve(data);
	  } catch (error) {
		 console.error(error);
		 reject(error);
	  }
	});
 }

export function GetData(pathValue, type, userData) {
	return new Promise((resolve, reject) => {
		fetch(Path(pathValue) + type, {
			method: "Get",
			body: JSON.stringify(userData),
			headers: {
				"Content-Type": "application/json",
				Authorization: "Bearer " + Cookies.get("_cb_search_token"),
			},
		}).then((response) => {
			trackApi("GET", type, response.status, response.status >= 400);
			return StatusValidation(response);
		})
		.then((responseJson) => {
			resolve(responseJson);
		}).catch((error) => {
			trackApi("GET", type, 0, true, error?.message);
			reject(error);
		});
	});
  }

/**
 * GET with Bearer token (no body). `type` is the path after the base URL, including query string.
 * Use for APIs that expect GET + query params (same auth pattern as PostData).
 */
export function GetRequest(pathValue, type, token = true) {
	return new Promise((resolve, reject) => {
		const header = {};
		setBearerIfPresent(header, token);
		fetch(Path(pathValue) + type, {
			method: "GET",
			headers: header,
		})
			.then((response) => {
				trackApi("GET", type, response.status, response.status >= 400);
				return StatusValidation(response);
			})
			.then((responseJson) => resolve(responseJson))
			.catch((error) => {
				trackApi("GET", type, 0, true, error?.message);
				console.log(error);
				reject(error);
			});
	});
}

/**
 * DELETE with Bearer token (no body). `type` is path + query string.
 * @param {{ soft403?: boolean }} [options] — when true, HTTP 403 returns JSON failure instead of forcing logout (for gateway blocks on DELETE).
 */
export function DeleteRequest(pathValue, type, token = true, options = {}) {
	const { soft403 = false } = options;
	return new Promise((resolve, reject) => {
		const header = { Accept: "application/json" };
		setBearerIfPresent(header, token);
		fetch(Path(pathValue) + type, {
			method: "DELETE",
			headers: header,
		})
			.then(async (response) => {
				trackApi("DELETE", type, response.status, response.status >= 400);
				if (soft403 && response.status === 403) {
					try {
						const json = await response.json();
						resolve(json);
					} catch {
						resolve({
							status: "failure",
							message: "Access forbidden",
						});
					}
					return;
				}
				return StatusValidation(response);
			})
			.then((responseJson) => {
				if (responseJson !== undefined) resolve(responseJson);
			})
			.catch((error) => {
				trackApi("DELETE", type, 0, true, error?.message);
				console.log(error);
				reject(error);
			});
	});
}

export const PutData = ( pathValue, type, userData, formData = false, token = true ) => {
	return new Promise((resolve, reject) => {
		let header = {};
		setBearerIfPresent(header, token);
		if (!formData) {
			header["Content-Type"] = "application/json";
		}
		fetch(Path(pathValue) + type, {
			method: "PUT",
			body: formData ? userData : JSON.stringify(userData),
			headers: header,
		}).then((response) => {
			return response.json();
		}).then((responseJson) => {
			resolve(responseJson);
		}).catch((error) => {
			console.error("Error:", error);
			reject(error);
		});
	});
};

export function DeleteData( pathValue, type, userData, formData = false, token = true ) {
	return new Promise((resolve, reject) => {
		let header = {};
		setBearerIfPresent(header, token);
		if (!formData) {
			header["Content-Type"] = "application/json";
		}
		fetch(Path(pathValue) + type, {
			method: "DELETE",
			body: formData ? userData : JSON.stringify(userData),
			headers: header,
		}).then((response) => {
			trackApi("DELETE", type, response.status, response.status >= 400);
			return StatusValidation(response);
		})
		.then((responseJson) => {
			resolve(responseJson);
		}).catch((error) => {
			trackApi("DELETE", type, 0, true, error?.message);
			console.log(error);
			reject(error);
		});
	});
}

export function PatchData(pathValue, type, userData, formData = false, token = true) {
	return new Promise((resolve, reject) => {
		const header = {};
		setBearerIfPresent(header, token);
		if (!formData) {
			header["Content-Type"] = "application/json";
		}
		fetch(Path(pathValue) + type, {
			method: "PATCH",
			body: formData ? userData : JSON.stringify(userData),
			headers: header,
		})
			.then((response) => {
				trackApi("PATCH", type, response.status, response.status >= 400);
				return StatusValidation(response);
			})
			.then((responseJson) => resolve(responseJson))
			.catch((error) => {
				trackApi("PATCH", type, 0, true, error?.message);
				console.log(error);
				reject(error);
			});
	});
}

export function PostDataWithApiKey(pathValue, type, userData, apiKey) {
	return new Promise((resolve, reject) => {
		let header = {
			"Content-Type": "application/json",
			"cb-api-key": apiKey
		};
		fetch(Path(pathValue) + type, {
			method: "POST",
			body: JSON.stringify(userData),
			headers: header,
		}).then((response) => StatusValidation(response))
		.then((responseJson) => {
			resolve(responseJson);
		}).catch((error) => {
			console.log(error);
			reject(error);
		});
	});
}
